import { useState } from "react";
import { useModulePresets } from "../../lib/modulePresets";
import { useAuth } from "../../lib/auth";
import { SUPPORTED_CURRENCIES } from "../../lib/currencies";
import { AuthFrame, AuthHead, IconField } from "./AuthFrame";
import { KindChoice, PlatformChoice } from "./BusinessTypePicker";

// Canvas order: name, what you run, what kind, first store, login.
const STEPS = ["business", "type", "preset", "store", "account"] as const;
type Step = (typeof STEPS)[number];

export function SignupWizard({ onSwitchToLogin }: { onSwitchToLogin: () => void }) {
  const { signup, loading, error } = useAuth();
  const { presets, isReady: presetsReady } = useModulePresets();

  const [stepIndex, setStepIndex] = useState(0);
  const step: Step = STEPS[stepIndex]!;

  const [businessName, setBusinessName] = useState("");
  const [platform, setPlatform] = useState<"pos" | "service">("pos");
  const [storeName, setStoreName] = useState("");
  const [address, setAddress] = useState("");
  const [currency, setCurrency] = useState("MMK");
  const [presetKey, setPresetKey] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pin, setPin] = useState("");
  const [localError, setLocalError] = useState("");

  const presetCards = presets.filter((p) => p.platform === platform);

  const goNext = () => {
    setLocalError("");
    if (step === "business" && businessName.trim().length < 2) return setLocalError("Enter at least 2 characters.");
    if (step === "preset" && !presetKey) return setLocalError("Pick the closest match.");
    if (step === "store" && !storeName.trim()) return setLocalError("Give your first store a name.");
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setLocalError("");
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const submit = () => {
    setLocalError("");
    if (!name.trim()) return setLocalError("Enter your name.");
    if (!/.+@.+\..+/.test(email)) return setLocalError("Enter a valid email address.");
    if (pw.length < 8) return setLocalError("Use at least 8 characters for your password.");
    if (pw !== confirmPw) return setLocalError("Passwords don't match.");
    if (!/^\d{4,8}$/.test(pin)) return setLocalError("Your PIN needs 4 to 8 digits.");
    if (!presetKey) return setLocalError("Go back and pick what kind of business you run.");
    void signup(businessName, storeName, email, pw, pin, name, presetKey, address, currency);
  };

  const blocked = loading || (step === "preset" && (!presetsReady || presetCards.length === 0));
  const stepLabel = `Step ${stepIndex + 1} of ${STEPS.length}`;

  return (
    <AuthFrame wide={step === "preset" || step === "account"}>
      <form
        className="o-auth__form"
        onSubmit={(e) => {
          e.preventDefault();
          if (step === "account") submit();
          else goNext();
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {stepIndex > 0 && (
            <button type="button" className="o-close" aria-label="Back" onClick={goBack}>
              <span className="ms" aria-hidden="true">arrow_back</span>
            </button>
          )}
          <span className="o-pill o-pill--accent">{stepLabel}</span>
        </div>

        {step === "business" && (
          <>
            <AuthHead title="What's your business called?" sub="Your brand name, shown on receipts and invoices." />
            <IconField label="Business name" icon="storefront" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Brew & Co" autoFocus />
          </>
        )}

        {step === "type" && (
          <>
            <AuthHead title="What do you run?" sub="Pick what your business does. This recommends the right modules." />
            <PlatformChoice
              value={platform}
              onChange={(p) => {
                setPlatform(p);
                setPresetKey(null);
              }}
            />
          </>
        )}

        {step === "preset" && (
          <>
            <AuthHead title="What kind of business?" sub="We switch on the modules that fit. You can change them later." />
            {!presetsReady ? (
              <div className="o-muted-note">Loading business types…</div>
            ) : presetCards.length === 0 ? (
              <div className="o-error" style={{ marginTop: 0 }}>Business types didn't load. Go back and try again in a moment.</div>
            ) : (
              <KindChoice presets={presetCards} value={presetKey} onChange={setPresetKey} />
            )}
          </>
        )}

        {step === "store" && (
          <>
            <AuthHead title="Name your first store" sub="Add more locations any time from Stores." />
            <IconField label="Store name" icon="storefront" value={storeName} onChange={(e) => setStoreName(e.target.value)} placeholder="Downtown" autoFocus />
            <IconField label="Address" icon="location_on" autoComplete="street-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Optional" />
            <div className="o-field" style={{ marginBottom: 0 }}>
              <div className="o-field__label">Currency</div>
              <div className="o-chips" role="radiogroup" aria-label="Currency">
                {SUPPORTED_CURRENCIES.map((c) => (
                  <button type="button" role="radio" aria-checked={currency === c.code} key={c.code} onClick={() => setCurrency(c.code)}>
                    {c.code}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {step === "account" && (
          <>
            <AuthHead title="Set up your login" sub="You'll use this for Owner Portal. The PIN signs you in on registers." />
            <div className="o-fields2">
              <IconField label="Your name" icon="person" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
              <IconField label="Your POS PIN" icon="pin" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="4 to 8 digits" />
            </div>
            <IconField label="Email" icon="mail" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.com" />
            <div className="o-fields2">
              <IconField label="Password" icon="lock" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="8+ characters" />
              <IconField label="Confirm password" icon="lock" type="password" autoComplete="new-password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
            </div>
          </>
        )}

        {(localError || error) && (
          <div className="o-error" role="alert" style={{ marginTop: 0 }}>
            {localError || error}
          </div>
        )}

        <button type="submit" className="o-auth__submit" disabled={blocked}>
          {loading ? "Creating your account…" : step === "account" ? "Create account" : "Continue"}
          {!loading && <span className="ms" aria-hidden="true">arrow_forward</span>}
        </button>
        {stepIndex === 0 && (
          <button type="button" className="o-link o-auth__alt" onClick={onSwitchToLogin}>
            Already have an account? Sign in
          </button>
        )}
      </form>
    </AuthFrame>
  );
}
